import React, {useMemo} from 'react'
import PropTypes from 'prop-types'

import {
    Box,
    Button,
    Text,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {useProduct} from '@salesforce/commerce-sdk-react'
import DisplayPrice from '../../../components/display-price'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {getPriceData} from '@salesforce/retail-react-app/app/utils/product-utils'
import {productUrlBuilder} from '../../../utils/url'

/**
 * The ProductTile is a simple visual representation of a
 * product object. It will show it's default image, name and price.
 * It also supports favourite products, controlled by a heart icon.
 */
export const ShopTheLookProductTile = (props) => {
    const {product} = props
    const {currency} = useCurrency()

    const productId = product

    const {data: productDetail} = useProduct(
        {
            parameters: {
                id: productId ? productId : product,
                perPricebook: true,
                allImages: true
            }
        },
        {
            // When shoppers select a different variant (and the app fetches the new data),
            // the old data is still rendered (and not the skeletons).
            keepPreviousData: true
        }
    )

    const priceData = useMemo(() => {
        return getPriceData(productDetail)
    }, [productDetail])
    const localizedProductName = productDetail?.name ?? productDetail?.productName
    const styles = useMultiStyleConfig('shopTheLookProductTile')

    return (
        <Box data-testid="shop-the-look-tile" {...styles.shopthelookTile} role="group">
            <Link
                to={productUrlBuilder({
                    productName: productDetail?.name,
                    productId: productDetail?.id
                })}
                aria-label={`${localizedProductName}`}
                {...styles.shopthelookTileLink}
            >
                <img
                    alt={localizedProductName}
                    className="chakra-image css-a076ck"
                    src={productDetail?.imageGroups[0]?.images[0].disBaseLink}
                    {...styles.shopthelookTileImg}
                />

                {/* Overlay */}
                <Box {...styles.shopthelookTileOverlay} _groupHover={{opacity: 1}}>
                    <Text {...styles.shopthelookTileText}>{localizedProductName}</Text>
                    <Box
                        {...styles.shopthelookTilePrize}
                        sx={{
                            '.current-price': {
                                color: 'white',
                                fontSize: 'md',
                                lineHeight: '24px',
                                fontWeight: 'extrabold'
                            }
                        }}
                    >
                        <DisplayPrice priceData={priceData} currency={currency} />
                    </Box>
                    <Button>Shop The Look</Button>
                </Box>
            </Link>
        </Box>
    )
}

ShopTheLookProductTile.displayName = 'ShopTheLookProductTile'

ShopTheLookProductTile.propTypes = {
    product: PropTypes.shape({
        currency: PropTypes.string,
        image: PropTypes.shape({
            alt: PropTypes.string,
            disBaseLink: PropTypes.string,
            link: PropTypes.string
        }),
        id: PropTypes.string,
        price: PropTypes.number,
        name: PropTypes.string,
        productName: PropTypes.string,
        productId: PropTypes.string,
        variants: PropTypes.array,
        type: PropTypes.shape({
            set: PropTypes.bool,
            bundle: PropTypes.bool,
            item: PropTypes.bool
        })
    })
}
